/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Logger } from "@html-core";

export type MessageHandler = (data: any, senderId: string) => void;

export class SharedWorkerChannelService {
   private worker: SharedWorker;
   private port: MessagePort;
   private connectionId: string | null = null;
   private messageHandlers: Map<string, MessageHandler> = new Map();
   private connectionReadyResolve!: (value: void | PromiseLike<void>) => void;
   private connectionReady: Promise<void>;
   private rawMessageQueue: Array<{ type: string; payload: any; targetId?: string }> = [];
   protected logger = new Logger("sharedWorkerChannel");
   constructor() {
      this.connectionReady = new Promise<void>((resolve) => {
         this.connectionReadyResolve = resolve;
      });

      this.initializeWorker();
   }

   private initializeWorker(): void {
      const workerUrl: string = "./sharedworker." + __BUILD_NUMBER__ + ".js";
      try {
         this.worker = new SharedWorker(workerUrl);
         this.port = this.worker.port;

         // Set up message handling
         this.port.onmessage = this.handleWorkerMessage.bind(this);
         this.port.start();
      } catch (error) {
         this.logger.error("Failed to initialize SharedWorker:" + error);
         this.notifyError("Failed to establish worker connection");
      }
   }

   private notifyError(errorMessage: string): void {
      if (this.messageHandlers.has("error")) {
         const handler = this.messageHandlers.get("error");
         if (handler) {
            handler({ error: errorMessage }, "system");
         }
      }
   }

   private handleWorkerMessage(event: MessageEvent): void {
      const message = event.data;

      switch (message.type) {
         case "connection-established":
            this.connectionId = message.connectionId;
            this.logger.debug(`Connected with ID: ${this.connectionId}`);
            this.connectionReadyResolve();
            this.processRawMessageQueue();
            break;

         case "message-error":
            this.logger.error("Message error:" + message.error);
            this.notifyError(message.error);
            break;

         default:
            this.processIncomingMessage(message);
            break;
      }
   }

   private processRawMessageQueue(): void {
      if (this.rawMessageQueue.length === 0) return;

      const queueCopy = [...this.rawMessageQueue];
      this.rawMessageQueue = [];

      for (const message of queueCopy) {
         this.sendRawMessage(message.type, message.payload, message.targetId).catch((error) => {
            this.logger.error("Failed to process queued message:" + error);
         });
      }
   }

   private processIncomingMessage(message: any): void {
      const handler = this.messageHandlers.get(message.type);
      if (handler) {
         handler(message.payload, message.senderId || "unknown");
      }
   }

   async sendRawMessage(type: string, payload: any, targetId?: string): Promise<void> {
      if (!this.connectionId) {
         this.rawMessageQueue.push({ type, payload, targetId });
         return;
      }

      const message: any = {
         type: type,
         payload: payload
      };

      if (targetId) {
         message.targetId = targetId;
      }

      this.port.postMessage(message);
   }

   onMessage(type: string, handler: MessageHandler): void {
      this.messageHandlers.set(type, handler);
   }

   async waitUntilReady(): Promise<void> {
      return this.connectionReady;
   }

   getConnectionId(): string | null {
      return this.connectionId;
   }

   close(): void {
      if (this.port) {
         this.port.close();
      }
   }
}
